import React from "react";
import ReactDOM from "react-dom/client";
import { AuthProvider } from "react-oidc-context";
import { BrowserRouter } from "react-router-dom";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { flagDefaults } from "@ld-scorecard/shared";
// Bootstrap + FlexAdmin theme CSS are loaded via <link> in index.html.
import "./index.css";
import App from "./App";
import { ConfigContext, loadRuntimeConfig } from "./config";
import { buildOidcConfig } from "./auth";
import { ThemeProvider } from "./theme";

async function bootstrap() {
  const config = await loadRuntimeConfig();
  const oidc = buildOidcConfig(config);

  // LaunchDarkly provider. Initialized with an anonymous context; App calls
  // identify() once the signed-in user is known. If no client-side ID is
  // configured, use a passthrough so the app still runs on flag defaults.
  const LDProvider = config.ldClientId
    ? await asyncWithLDProvider({
        clientSideID: config.ldClientId,
        context: { kind: "user", key: "anonymous", anonymous: true },
        flags: flagDefaults,
        reactOptions: { useCamelCaseFlagKeys: false },
      })
    : ({ children }: { children: React.ReactNode }) => <>{children}</>;

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <ThemeProvider>
        <ConfigContext.Provider value={config}>
          <LDProvider>
            <AuthProvider {...oidc}>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </AuthProvider>
          </LDProvider>
        </ConfigContext.Provider>
      </ThemeProvider>
    </React.StrictMode>,
  );
}

void bootstrap();
